import React from 'react';
import { Link } from 'react-router-dom';
import { Lightbulb } from 'lucide-react';

/** Renders data-driven content blocks shared by the guide and legal pages. */
export const ContentBlocks = ({ blocks, testIdPrefix }) => (
  <>
    {blocks.map((b, i) => {
      const key = `${testIdPrefix}-${i}`;
      if (b.type === 'h3') return <h3 key={key} className="text-lg font-semibold text-white mt-8">{b.text}</h3>;
      if (b.type === 'ul') {
        return (
          <ul key={key} className="space-y-2.5 my-5 pl-1">
            {b.items.map((it, j) => (
              <li key={j} className="flex items-start gap-3 text-[15px] text-white/70 leading-relaxed">
                <span className="mt-[9px] w-1.5 h-1.5 rounded-full bg-[var(--lux-gold)] shrink-0" aria-hidden="true" />
                <span>{it}</span>
              </li>
            ))}
          </ul>
        );
      }
      if (b.type === 'callout') {
        return (
          <aside key={key} className="my-6 rounded-2xl border border-lux-gold/25 bg-lux-gold/5 p-5 flex gap-4" data-testid={`${key}-callout`}>
            <Lightbulb className="w-5 h-5 text-[var(--lux-gold)] shrink-0 mt-0.5" aria-hidden="true" />
            <div>
              <p className="text-sm font-semibold text-white">{b.title}</p>
              <p className="mt-1 text-sm text-white/65 leading-relaxed">{b.text}</p>
            </div>
          </aside>
        );
      }
      if (b.type === 'table') {
        return (
          <dl key={key} className="my-5 rounded-2xl border border-white/10 bg-lux-panel/90 divide-y divide-white/[0.08] overflow-hidden">
            {b.rows.map(([k, v]) => (
              <div key={k} className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-4 px-5 py-3">
                <dt className="text-xs uppercase tracking-wider text-white/40 sm:pt-0.5">{k}</dt>
                <dd className="sm:col-span-2 text-sm text-white/80 break-words">{v}</dd>
              </div>
            ))}
          </dl>
        );
      }
      return <p key={key} className="text-[15px] text-white/70 leading-relaxed my-4">{b.text}</p>;
    })}
  </>
);

/** Sticky table of contents (anchors to section ids). */
export const TableOfContents = ({ title, sections, testId = 'toc' }) => (
  <nav aria-label={title} className="rounded-2xl border border-white/10 bg-lux-panel/90 p-5" data-testid={testId}>
    <p className="text-[11px] uppercase tracking-[0.2em] text-white/40 mb-3">{title}</p>
    <ol className="space-y-2">
      {sections.map((s, i) => (
        <li key={s.id}>
          <a href={`#${s.id}`} className="flex items-start gap-2 text-sm text-white/70 hover:text-[var(--lux-gold)] transition-colors duration-200" data-testid={`${testId}-link-${s.id}`}>
            <span className="text-white/30 tabular-nums w-5 shrink-0">{String(i + 1).padStart(2, '0')}</span>
            <span>{s.title}</span>
          </a>
        </li>
      ))}
    </ol>
  </nav>
);

export const RelatedLinks = ({ title, links, testId = 'related-links' }) => (
  <div className="rounded-2xl border border-white/10 bg-lux-panel/90 p-5" data-testid={testId}>
    <p className="text-[11px] uppercase tracking-[0.2em] text-white/40 mb-3">{title}</p>
    <ul className="space-y-2">
      {links.map(l => (
        <li key={l.to}><Link to={l.to} className="text-sm text-white/70 hover:text-[var(--lux-gold)] transition-colors duration-200" data-testid={`${testId}-${l.key}`}>{l.label}</Link></li>
      ))}
    </ul>
  </div>
);
